import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { inputCls, canDo } from "./admin/shared";
import { ProductsTab } from "./admin/ProductsTab";
import { CategoriesTab } from "./admin/CategoriesTab";
import { StaffTab } from "./admin/StaffTab";
import { RolesTab } from "./admin/RolesTab";
import { AuditTab } from "./admin/AuditTab";
import { BuilderTab } from "./admin/BuilderTab";
import { MediaTab } from "./admin/MediaTab";
import { SizeChartsTab } from "./admin/SizeChartsTab";

function Overview() {
  const [range, setRange] = useState("30d");
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/admin/dashboard?period=${range}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [range]);

  if (!d) return <p className="text-vv-muted text-sm py-10 animate-pulse">Loading dashboard…</p>;
  const maxRev = Math.max(1, ...d.series.map((s) => s.revenue));
  const statuses = ["placed", "confirmed", "shipped", "delivered", "cancelled"];

  return (
    <div data-testid="admin-dashboard" className="space-y-10">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Revenue", inr(d.revenue)], ["Orders", d.orders], ["Customers", d.customers], ["Live Products", d.products], ["Out of Stock", d.out_of_stock]].map(([label, val]) => (
          <div key={label} className="border border-vv-black/10 p-5">
            <p className="vv-overline text-vv-muted">{label}</p>
            <p className="font-display text-2xl md:text-3xl mt-2">{val}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {statuses.map((s) => (
          <span key={s} data-testid={`status-count-${s}`} className="text-[10px] uppercase tracking-[0.2em] border border-vv-black/15 px-3 py-1.5">
            {s}: <b>{d.status_counts[s] || 0}</b>
          </span>
        ))}
      </div>

      <div className="border border-vv-black/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <p className="vv-overline text-vv-muted">Sales Overview</p>
          <div className="flex gap-2">
            {[["7d", "7 Days"], ["30d", "30 Days"], ["90d", "90 Days"]].map(([val, label]) => (
              <button key={val} data-testid={`range-${val}`} onClick={() => setRange(val)}
                className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1.5 transition-colors ${range === val ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div data-testid="sales-chart" className="flex items-end gap-[2px] h-40">
          {d.series.map((s) => (
            <div key={s.date} className="flex-1 flex flex-col justify-end group relative" title={`${s.date}: ${inr(s.revenue)} · ${s.orders} orders`}>
              <div className="bg-vv-black/80 group-hover:bg-vv-espresso transition-colors w-full" style={{ height: `${Math.max(2, (s.revenue / maxRev) * 100)}%` }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[9px] text-vv-stone mt-2 font-mono">
          <span>{d.series[0]?.date}</span><span>{d.series[d.series.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-vv-black/10 p-6">
          <p className="vv-overline text-vv-muted mb-4">Best Sellers</p>
          {d.best_sellers.length === 0 && <p className="text-sm text-vv-muted">No sales data yet.</p>}
          <div className="space-y-3">
            {d.best_sellers.map((b, i) => (
              <div key={b.name} className="flex justify-between text-sm">
                <span className="truncate"><span className="text-vv-stone mr-2">{i + 1}.</span>{b.name}</span>
                <span className="shrink-0 text-vv-muted">{b.qty} sold · {inr(b.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-vv-black/10 p-6">
          <p className="vv-overline text-vv-muted mb-4">Low Stock Alerts</p>
          {d.low_stock.length === 0 && <p className="text-sm text-vv-muted">All products well stocked.</p>}
          <div className="space-y-3">
            {d.low_stock.map((p) => (
              <div key={p.id} className="flex justify-between text-sm">
                <span className="truncate">{p.name}</span>
                <span className="shrink-0 text-vv-burgundy font-medium">{p.stock} left</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-vv-black/10 p-6">
          <p className="vv-overline text-vv-muted mb-4">Recent Orders</p>
          <div className="space-y-3">
            {d.recent_orders.map((o) => (
              <div key={o.id} className="flex justify-between text-sm">
                <span className="font-mono">{o.ref}</span>
                <span className="text-vv-muted shrink-0">{o.customer?.name} · {inr(o.total)} · {o.status}</span>
              </div>
            ))}
            {d.recent_orders.length === 0 && <p className="text-sm text-vv-muted">No orders yet.</p>}
          </div>
        </div>
        <div className="border border-vv-black/10 p-6">
          <p className="vv-overline text-vv-muted mb-4">Newest Customers</p>
          <div className="space-y-3">
            {d.recent_customers.map((u) => (
              <div key={u.id} className="flex justify-between text-sm">
                <span className="truncate">{u.name || "—"}</span>
                <span className="text-vv-muted shrink-0">{u.email || (u.phone ? `+91 ${u.phone.replace(/^91/, "")}` : "")}</span>
              </div>
            ))}
            {d.recent_customers.length === 0 && <p className="text-sm text-vv-muted">No customers yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomersTab() {
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = (query = "") => api.get(`/admin/customers${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((r) => setCustomers(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const open = async (c) => {
    try {
      const { data } = await api.get(`/admin/customers/${c.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-3 mb-8 max-w-md">
        <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, mobile or email" className={inputCls} />
        <button data-testid="customer-search-button" type="submit" className="vv-btn-dark !px-5">Search</button>
      </form>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-vv-muted border-b border-vv-black/10">
              <th className="py-3 pr-4">Customer</th><th className="pr-4">Contact</th><th className="pr-4">Joined</th><th className="pr-4">Orders</th><th className="pr-4">Total Spend</th><th className="pr-4">Last Order</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => open(c)} className="border-b border-vv-black/5 cursor-pointer hover:bg-vv-elevated/40 transition-colors">
                <td className="py-3 pr-4 font-medium">{c.name || "—"}</td>
                <td className="pr-4 text-vv-muted">{c.email || (c.phone ? `+91 ${c.phone.replace(/^91/, "")}` : "—")}</td>
                <td className="pr-4 text-vv-muted">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : "—"}</td>
                <td className="pr-4">{c.orders}</td>
                <td className="pr-4 font-medium">{inr(c.spend)}</td>
                <td className="pr-4 text-vv-muted">{c.last_order ? new Date(c.last_order).toLocaleDateString("en-IN") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {customers.length === 0 && <p data-testid="customers-empty" className="text-sm text-vv-muted py-10">No customers found.</p>}
      </div>

      {detail && (
        <div className="fixed inset-0 z-[80] flex justify-end">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setDetail(null)} />
          <div data-testid="customer-detail" className="relative bg-white w-full max-w-md h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-8">
              <p className="font-display text-2xl">{detail.name || "Customer"}</p>
              <button data-testid="customer-detail-close" onClick={() => setDetail(null)} aria-label="Close"><X size={20} strokeWidth={1.25} /></button>
            </div>
            <div className="text-sm space-y-1.5 text-vv-muted">
              {detail.email && <p>{detail.email}</p>}
              {detail.phone && <p>+91 {detail.phone.replace(/^91/, "")} {detail.phone_verified && <span className="text-vv-forest text-[10px] uppercase tracking-widest">verified</span>}</p>}
              <p>Joined {detail.created_at ? new Date(detail.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—"}</p>
            </div>
            <p className="vv-overline text-vv-muted mt-8 mb-3">Saved Addresses</p>
            {(detail.addresses || []).length === 0 && <p className="text-sm text-vv-muted">None saved.</p>}
            <div className="space-y-3">
              {(detail.addresses || []).map((a) => (
                <div key={a.id} className="border border-vv-black/10 p-4 text-sm text-vv-muted">
                  <p className="text-vv-black font-medium">{a.name} <span className="text-[9px] uppercase tracking-widest text-vv-muted border border-vv-black/15 px-1.5 py-0.5 ml-1">{a.label}</span></p>
                  <p className="mt-1">{a.house}, {a.street}, {a.city}, {a.state} — {a.pin}</p>
                </div>
              ))}
            </div>
            <p className="vv-overline text-vv-muted mt-8 mb-3">Order History ({detail.orders?.length || 0})</p>
            <div className="space-y-3">
              {(detail.orders || []).map((o) => (
                <div key={o.id} className="border border-vv-black/10 p-4 text-sm flex justify-between">
                  <div>
                    <p className="font-mono">{o.ref}</p>
                    <p className="text-[11px] text-vv-muted mt-1">{new Date(o.created_at).toLocaleDateString("en-IN")} · {o.items?.length} item(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{inr(o.total)}</p>
                    <p className="text-[10px] uppercase tracking-widest text-vv-muted mt-1">{o.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab({ isAdmin }) {
  const [orders, setOrders] = useState([]);
  const [refundFor, setRefundFor] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNote, setRefundNote] = useState("Customer requested refund");
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (o, status) => {
    await api.patch(`/admin/orders/${o.id}`, { status }).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const refund = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/orders/${refundFor.id}/refund`, { amount: parseFloat(refundAmount), note: refundNote });
      toast.success(`Refund ${data.refund_id} initiated (${data.status})`);
      setRefundFor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const payBadge = (o) => {
    if (o.payment_method !== "cashfree") return null;
    if (o.status === "placed" && o.paid_at) return <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 bg-vv-forest text-vv-ivory ml-2">Paid online{o.payment_env !== "production" ? " (test)" : ""}</span>;
    if (o.status === "payment_pending") return <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 bg-vv-stone/40 ml-2">Awaiting payment</span>;
    if (o.status === "cancelled" && o.cancel_reason?.includes("payment")) return <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 bg-vv-burgundy text-vv-ivory ml-2">Payment not completed</span>;
    return null;
  };

  return (
    <div className="space-y-4">
      {orders.length === 0 && <p data-testid="admin-orders-empty" className="text-vv-muted text-sm py-10">No orders yet.</p>}
      {orders.map((o) => (
        <div key={o.id} data-testid={`admin-order-${o.ref}`} className="border border-vv-black/10 p-5 flex flex-wrap gap-6 items-center justify-between">
          <div>
            <p className="font-mono text-sm">{o.ref}{payBadge(o)}</p>
            <p className="text-[11px] text-vv-muted mt-1">{o.customer?.name} · {o.customer?.email || (o.customer?.phone ? `+91 ${o.customer.phone.replace(/^91/, "")}` : "")} · {o.customer?.city}</p>
            <p className="text-[11px] text-vv-muted">
              {o.items?.length} item(s) · {inr(o.total)}{o.discount > 0 ? ` (saved ${inr(o.discount)} via ${o.coupon})` : ""} · {o.payment_method?.toUpperCase()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && o.payment_method === "cashfree" && o.status === "placed" && o.paid_at && (
              <button data-testid={`admin-refund-${o.ref}`} onClick={() => { setRefundFor(o); setRefundAmount(String(o.total)); }}
                className="text-[10px] uppercase tracking-[0.2em] border border-vv-burgundy text-vv-burgundy px-3 py-2 hover:bg-vv-burgundy hover:text-vv-ivory transition-colors">
                Refund
              </button>
            )}
            <select data-testid={`admin-order-status-${o.ref}`} value={o.status} onChange={(e) => setStatus(o, e.target.value)}
              className="bg-transparent border border-vv-black/20 text-[11px] uppercase tracking-[0.2em] px-3 py-2 outline-none">
              {["payment_pending", "placed", "confirmed", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      ))}

      {refundFor && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setRefundFor(null)} />
          <form data-testid="refund-form" onSubmit={refund} className="relative bg-white w-full max-w-sm p-8 space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-display text-xl">Refund {refundFor.ref}</p>
              <button type="button" onClick={() => setRefundFor(null)} aria-label="Close"><X size={18} strokeWidth={1.25} /></button>
            </div>
            <p className="text-[11px] text-vv-muted">Paid {inr(refundFor.total)} online. Full or partial refunds go back to the original payment method.</p>
            <input data-testid="refund-amount" required type="number" min="1" max={refundFor.total} step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder="Amount (INR)" className={inputCls} />
            <input data-testid="refund-note" value={refundNote} onChange={(e) => setRefundNote(e.target.value)} placeholder="Note" className={inputCls} />
            <button data-testid="refund-submit" type="submit" className="vv-btn-dark w-full">Initiate Refund</button>
          </form>
        </div>
      )}
    </div>
  );
}

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [cform, setCform] = useState({ code: "", type: "percent", value: "", min_order: "" });

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { code: cform.code, type: cform.type, value: parseFloat(cform.value) || 0, min_order: parseFloat(cform.min_order) || 0 });
      toast.success("Coupon created");
      setCform({ code: "", type: "percent", value: "", min_order: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    await api.delete(`/admin/coupons/${code}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <div className="max-w-2xl">
      <p className="vv-overline mb-4">Create Discount Code</p>
      <form data-testid="coupon-form" onSubmit={save} className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
        <input data-testid="coupon-code-input" required value={cform.code} onChange={(e) => setCform({ ...cform, code: e.target.value.toUpperCase() })} placeholder="CODE" maxLength={20} className={`${inputCls} uppercase tracking-widest`} />
        <select data-testid="coupon-type-select" value={cform.type} onChange={(e) => setCform({ ...cform, type: e.target.value })} className={inputCls}>
          <option value="percent">% Off</option>
          <option value="flat">₹ Flat Off</option>
        </select>
        <input data-testid="coupon-value-input" required type="number" min="1" value={cform.value} onChange={(e) => setCform({ ...cform, value: e.target.value })} placeholder={cform.type === "percent" ? "Percent" : "Amount ₹"} className={inputCls} />
        <input data-testid="coupon-min-input" type="number" min="0" value={cform.min_order} onChange={(e) => setCform({ ...cform, min_order: e.target.value })} placeholder="Min order ₹" className={inputCls} />
        <button data-testid="coupon-create-button" type="submit" className="vv-btn-dark !px-4">Create</button>
      </form>
      {coupons.length === 0 && <p data-testid="coupons-empty" className="text-vv-muted text-sm">No discount codes yet.</p>}
      <div className="space-y-3">
        {coupons.map((c) => (
          <div key={c.id} data-testid={`coupon-row-${c.code}`} className="border border-vv-black/10 p-4 flex items-center justify-between gap-4">
            <div>
              <p className="font-mono text-sm tracking-widest">{c.code}</p>
              <p className="text-[11px] text-vv-muted mt-1">{c.type === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}{c.min_order > 0 ? ` · min order ${inr(c.min_order)}` : ""}</p>
            </div>
            <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c.code)} aria-label="Delete coupon" className="text-vv-muted hover:text-vv-burgundy transition-colors">
              <Trash2 size={15} strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [announcement, setAnnouncement] = useState("");
  useEffect(() => { api.get("/settings/public").then((r) => setAnnouncement(r.data.announcement)).catch(() => {}); }, []);
  const save = async () => {
    await api.put("/admin/settings", { announcement }).catch((e) => toast.error(formatApiError(e)));
    toast.success("Announcement updated");
  };
  return (
    <div className="max-w-xl">
      <p className="vv-overline mb-4">Announcement Bar</p>
      <textarea data-testid="admin-announcement-input" value={announcement} onChange={(e) => setAnnouncement(e.target.value)} rows={3} className={inputCls} />
      <button data-testid="admin-announcement-save" onClick={save} className="vv-btn-dark mt-4">Save</button>
    </div>
  );
}

export default function Admin() {
  const { user, authChecked } = useShop();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [categories, setCategories] = useState([]);

  const isStaff = authChecked && ["admin", "staff"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (authChecked && !["admin", "staff"].includes(user?.role)) navigate("/auth");
  }, [authChecked, user, navigate]);

  const loadCats = () => api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  useEffect(() => { if (isStaff) loadCats(); }, [isStaff]);

  if (!isStaff) return <main className="min-h-[50vh]" />;

  const canView = (m) => canDo(user, m, "view");
  const NAV = [
    { group: "Store", items: [
      ...(canView("dashboard") ? [["overview", "Overview"]] : []),
      ...(canView("products") ? [["products", "Products"], ["sizecharts", "Size Charts"]] : []),
      ...(canView("categories") ? [["categories", "Collections"]] : []),
      ...(canView("orders") ? [["orders", "Orders"]] : []),
      ...(canView("customers") ? [["customers", "Customers"]] : []),
    ]},
    { group: "Content", items: [
      ...(canView("content") ? [["builder", "Website & Homepage"], ["media", "Media Library"]] : []),
    ]},
    ...(isAdmin ? [
      { group: "Marketing", items: [["coupons", "Discounts"]] },
      { group: "Administration", items: [["staff", "Staff"], ["roles", "Roles & Permissions"], ["audit", "Activity Log"], ["settings", "Settings"]] },
    ] : []),
  ].filter((g) => g.items.length);
  const allTabs = NAV.flatMap((g) => g.items);
  const active = allTabs.some(([key]) => key === tab) ? tab : allTabs[0]?.[0];
  const roleLabel = isAdmin ? "Owner" : (user?.role_key || "staff").replace(/_/g, " ");

  return (
    <main data-testid="admin-page" className="px-4 sm:px-8 lg:px-12 max-w-[1600px] mx-auto py-10 md:py-14 min-h-screen">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <p className="vv-overline text-vv-muted mb-3">Velour &amp; Vane Studio · <span data-testid="admin-role-label" className="capitalize">{roleLabel}</span></p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">ADMIN</h1>
        </div>
        <Link to="/" className="vv-btn-outline-dark">View Store</Link>
      </div>

      {/* Mobile module picker */}
      <select data-testid="admin-module-select" value={active || ""} onChange={(e) => setTab(e.target.value)}
        className={`${inputCls} md:hidden mb-8`}>
        {NAV.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.items.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </optgroup>
        ))}
      </select>

      <div className="flex gap-10 items-start">
        <aside data-testid="admin-sidebar" className="hidden md:block w-56 shrink-0 sticky top-24">
          {NAV.map((g) => (
            <div key={g.group} className="mb-7">
              <p className="text-[9px] uppercase tracking-[0.3em] text-vv-stone mb-2 px-3">{g.group}</p>
              {g.items.map(([key, label]) => (
                <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
                  className={`block w-full text-left px-3 py-2 text-[12px] tracking-wide transition-colors border-l-2 ${active === key ? "border-vv-black font-medium" : "border-transparent text-vv-muted hover:text-vv-black"}`}>
                  {label}
                </button>
              ))}
            </div>
          ))}
        </aside>

        <div className="flex-1 min-w-0">
          {active === "overview" && <Overview />}
          {active === "products" && <ProductsTab user={user} categories={categories} />}
          {active === "sizecharts" && <SizeChartsTab user={user} />}
          {active === "categories" && <CategoriesTab user={user} reloadCats={loadCats} />}
          {active === "customers" && <CustomersTab />}
          {active === "orders" && <OrdersTab isAdmin={isAdmin} />}
          {active === "builder" && <BuilderTab user={user} />}
          {active === "media" && <MediaTab user={user} />}
          {active === "coupons" && isAdmin && <CouponsTab />}
          {active === "staff" && isAdmin && <StaffTab />}
          {active === "roles" && isAdmin && <RolesTab />}
          {active === "audit" && isAdmin && <AuditTab />}
          {active === "settings" && isAdmin && <SettingsTab />}
        </div>
      </div>
    </main>
  );
}
